// @ts-nocheck

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useRef, useEffect } from "react";
import { Field, useField } from "formik";
import clsx from "clsx";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import { BsEyeFill, BsEyeSlashFill } from "react-icons/bs";
import { ChevronDown, Check, X } from "lucide-react";
import { InputFieldProps } from "../../../interfaces";
import { DatePicker } from "antd";
import moment from "moment";

// Extended InputFieldProps to include new properties for select and country inputs
interface ExtendedInputFieldProps extends InputFieldProps {
  countryOptions?: Array<{ value: string; label: string; flag?: string }>;
  currencyOptions?: Array<{ value: string; label: string; symbol?: string }>;
  showTimeSelect?: boolean; // New prop to control time selection
}

// Option type for select inputs
interface SelectOption {
  value: string;
  label: string;
  symbol?: string;
  flag?: string;
}

const InputField: React.FC<ExtendedInputFieldProps> = ({
  label,
  note,
  icon: Icon,
  type = "text",
  options = [],
  countryOptions = [],
  currencyOptions = [],
  prepend,
  append,
  placeholder,
  showTimeSelect = false,
  searchLabel = "Search",
  ...props
}) => {
  const [field, meta, helpers] = useField(props.name);
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const selectRef = useRef<HTMLDivElement>(null);
  const hasValue = Boolean(field.value);
  const showError = meta.touched && meta.error;

  // Input base styling
  const inputBase = clsx(
    "w-full rounded-lg flex flex-col items-center px-3 py-2 bg-white",
    "text-gray-900 border transition-all focus:outline-none z-1",
    "placeholder-gray-400 dark:placeholder-gray-500",
    {
      "border-gray-300 dark:border-gray-700 focus:border-primary focus:ring-1 focus:ring-primary dark:focus:ring-primary":
        !showError,
      "border-[#a51212] focus:border-[#a51212] focus:ring-[#a51212] dark:focus:ring-[#a51212]":
        showError,

      "pl-14": ["tel", "country"].includes(type),
      "pr-10": append || type === "password" || type === "select",
    },
  );

  const selectBase = clsx(
    "w-full rounded-[16px] px-3 py-2 bg-white cursor-pointer",
    "text-gray-900 border transition-all outline-none flex items-center justify-between",
    "placeholder-gray-400 dark:placeholder-gray-500",
    {
      "border-gray-300 dark:border-gray-700 focus:border border-[] ring-1 ring-[]":
        isSelectOpen && !showError,
      "border-gray-300 dark:border-gray-700 hover:border-[]":
        !isSelectOpen && !showError,
      "border-[#a51212] ring-1 ring-[#a51212]": showError,
    },
  );

  // Updated labelBase to properly handle select type
  const labelBase = clsx(
    "transition-all duration-200 pointer-events-none z-9 text-sm mb-[20px] font-semibold",
    showError ? "text-[#a51212]" : "text-[#31383e]",
    {
      // For all inputs except tel, country, select
      "left-12 top-4":
        (Icon || prepend) &&
        !isFocused &&
        !hasValue &&
        !["tel", "country", "select"].includes(type),
      "left-3 top-4":
        !Icon &&
        !prepend &&
        !isFocused &&
        !hasValue &&
        !["tel", "country", "select"].includes(type),

      // For tel and country
      "left-14 top-2.5":
        ["tel", "country"].includes(type) && !isFocused && !hasValue,

      // For select when not focused and no value
      "left-10 top-4":
        type === "select" && (Icon || prepend) && !isSelectOpen && !hasValue,
      /* @ts-ignore */
      // eslint-disable-next-line no-dupe-keys
      "left-3 top-4":
        type === "select" && !Icon && !prepend && !isSelectOpen && !hasValue,

      // For all inputs except tel, country, select when focused or has value
      "left-10 -top-2.5 text-sm bg-white":
        (Icon || prepend) &&
        (isFocused || hasValue) &&
        !["tel", "country"].includes(type),
      "left-3 -top-2.5 text-sm bg-white":
        !Icon &&
        !prepend &&
        (isFocused || hasValue) &&
        !["tel", "country"].includes(type),

      // For tel and country when focused or has value
      "left-14 -top-2.5 text-sm bg-white":
        ["tel", "country"].includes(type) && (isFocused || hasValue),
    },
  );

  //

  const handleChange = (date) => {
    // Store date as ISO string in Formik
    helpers.setValue(date ? date.toISOString() : "");
  };

  // Close select dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsSelectOpen(false);
        helpers.setTouched(true);
      }
    };

    if (isSelectOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSelectOpen, helpers]);

  const handlePhoneChange = (value: string) => {
    helpers.setValue(`+${value}`);
  };

  const handleCountryChange = (value: string, country: any) => {
    helpers.setValue(country?.iso2 || value);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSelectClick = () => {
    setIsSelectOpen(!isSelectOpen);
    setIsFocused(!isSelectOpen);
    if (!isSelectOpen) {
      setSearchQuery("");
    }
  };

  const handleOptionSelect = (value: string) => {
    helpers.setValue(value);
    setIsSelectOpen(false);
    helpers.setTouched(true);
  };

  // const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   setSearchQuery(e.target.value);
  // };

  const clearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    helpers.setValue("");
  };

  // Get the display label for the selected option
  const getSelectedOptionLabel = (): string => {
    const selectOptions = type === "select" ? currencyOptions : options;
    const selectedOption = selectOptions.find(
      (opt) => opt.value === field.value,
    );
    return selectedOption ? selectedOption.label : "";
  };

  // Filter options based on search query
  const getFilteredOptions = (): SelectOption[] => {
    const selectOptions = type === "select" ? currencyOptions : options;
    if (!searchQuery) {
      return selectOptions.map((option) => ({
        ...option,
        value: String(option.value), // Ensure value is a string
      }));
    }

    return selectOptions
      .filter((option) =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase()),
      )
      .map((option) => ({
        ...option,
        value: String(option.value), // Ensure value is a string
      }));
  };

  const renderEnhancedSelect = () => {
    const filteredOptions = getFilteredOptions();
    const hasSelectedValue = Boolean(field.value);
    const selectedLabel = getSelectedOptionLabel();

    return (
      <div className="relative w-full" ref={selectRef}>
        {label && (
          <label
            className={clsx(
              labelBase,

              {
                "left-10 -top-2.5 text-sm bg-white":
                  (Icon || prepend) && (isSelectOpen || hasSelectedValue),
                "left-3 -top-2.5 text-sm bg-white":
                  !Icon && !prepend && (isSelectOpen || hasSelectedValue),
              },
            )}
          >
            {label}
          </label>
        )}

        {/* Select trigger button */}
        <div
          className={selectBase}
          onClick={handleSelectClick}
          // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
          role="combobox"
          aria-expanded={isSelectOpen}
          aria-haspopup="listbox"
          tabIndex={0}
        >
          <span
            className={clsx(
              "truncate",
              !hasSelectedValue && "text-text",
              "pl-0",
            )}
          >
            {selectedLabel || placeholder || "Select an option"}
          </span>
          <div className="flex items-center">
            {hasSelectedValue && (
              <button
                type="button"
                onClick={clearSelection}
                className="p-1 mr-1 hover:bg-gray-100 rounded-full"
                aria-label="Clear selection"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            )}
            <ChevronDown
              className={clsx(
                "h-5 w-5 text-gray-500 transition-transform",
                isSelectOpen && "transform rotate-180",
              )}
            />
          </div>
        </div>

        {/* Dropdown panel */}
        {isSelectOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white rounded-[16px] shadow-lg border border-gray-200 max-h-60 overflow-auto">
            {/* Search input */}
            <div className="sticky top-0 p-2 bg-white border-b border-gray-200">
              {/* <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder={`${searchLabel}...`}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-[16px] focus:outline-none focus:ring-1 focus:ring-[] focus:border-[]"
                  autoFocus
                />
              </div> */}
            </div>

            {/* Options list */}
            <ul className="py-1" role="listbox">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <li
                    key={option.value}
                    role="option"
                    aria-selected={field.value === option.value}
                    onClick={() => handleOptionSelect(option.value)}
                    className={clsx(
                      "px-3 py-2 flex items-center cursor-pointer",
                      "hover:bg-gray-100",
                      field.value === option.value ? "bg-gray-50" : "",
                    )}
                  >
                    {option.symbol && (
                      <span className="mr-2 text-gray-700">
                        {option.symbol}
                      </span>
                    )}
                    {option.flag && <span className="mr-2">{option.flag}</span>}
                    <span className="flex-grow">{option.label}</span>
                    {field.value === option.value && (
                      <Check className="h-4 w-4 text-[]" />
                    )}
                  </li>
                ))
              ) : (
                <li className="px-3 py-2 text-gray-500 text-center">
                  No options found
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderInput = () => {
    switch (type) {
      case "tel":
        return (
          <PhoneInput
            country="ug"
            value={field.value}
            countryCodeEditable={false}
            onChange={handlePhoneChange}
            onBlur={() => helpers.setTouched(true)}
            inputProps={{
              name: field.name,
              required: props.required,
              className: clsx(inputBase, "!w-full ", "placeholder:pl-2"),
              placeholder: placeholder || " ",
            }}
            buttonClass={clsx(
              "!bg-transparent !border-0 !rounded-l-md !p-2 !left-1",
              "",
              "flex items-center gap-2",
            )}
            // dropdownClass="dark:bg-gray-800 dark:text-white dark:border-gray-700"
            preferredCountries={["ug"]}
            disableSearchIcon
            onlyCountries={["ug"]}
          />
        );
      // @ts-ignore
      case "country":
        return (
          <div className="relative">
            {Icon && (
              <div className="absolute top-[18px] left-[70px] z-10 pointer-events-none">
                {/* @ts-ignore */}
                <Icon className="text-gray-400 dark:text-gray-500 w-5 h-5" />
              </div>
            )}
            <PhoneInput
              country="ke"
              value={field.value}
              onChange={handleCountryChange}
              onBlur={() => helpers.setTouched(true)}
              inputProps={{
                name: field.name,
                required: props.required,
                className: clsx(inputBase, "!w-full ", "placeholder:pl-2"),
                placeholder: placeholder || "Select Country",
              }}
              buttonClass={clsx(
                "!bg-transparent !border-0 !rounded-l-md !p-2 !left-1",
                "",
                "flex items-center gap-2",
              )}
              // dropdownClass="dark:bg-gray-800 dark:text-white dark:border-gray-700"
              preferredCountries={["ke", "us", "gb", "ca", "au"]}
              enableSearch
              disableSearchIcon
              inputStyle={{ paddingLeft: Icon ? "100px" : "88px" }}
              // This disables actual phone input functionality
              specialLabel=""
              disableDropdown={false}
              countryCodeEditable={false}
            />
          </div>
        );

      case "select":
        return renderEnhancedSelect();

      case "date":
        return (
          <div className="relative w-full">
            <DatePicker
              className={clsx(inputBase, "placeholder:pl-1")}
              format="DD/MM/YYYY"
              placeholder={placeholder}
              onFocus={() => setIsFocused(true)}
              onBlur={(e) => {
                helpers.setTouched(true);
                setIsFocused(false);
              }}
              style={{ width: "100%" }}
              onChange={handleChange}
              value={field.value ? moment(field.value) : null}
            />
          </div>
        );

      case "password":
        return (
          <>
            <input
              {...field}
              {...props}
              type={showPassword ? "text" : "password"}
              className={clsx(inputBase, "placeholder:pl-1")}
              placeholder={placeholder}
              onFocus={() => setIsFocused(true)}
              onBlur={(e) => {
                field.onBlur(e);
                setIsFocused(false);
              }}
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute top-[34px] right-0 pr-3 flex items-center  cursor-pointer"
            >
              {showPassword ? (
                // @ts-ignore
                <BsEyeFill className="h-5 w-5 text-[#31383E]  " />
              ) : (
                // @ts-ignore
                <BsEyeSlashFill className="h-5 w-5 text-[#31383E]" />
              )}
            </button>
          </>
        );

      case "radio":
        return (
          <>
            {label && (
              <label className={clsx(labelBase, "mb-2")}>{label}</label>
            )}
            <div className="space-y-1 space-x-4">
              {options.map((opt) => (
                <label
                  key={opt.value as string}
                  className="inline-flex items-center space-x-2"
                >
                  <input
                    type="radio"
                    {...field}
                    value={opt.value as string}
                    checked={
                      typeof field.value === "boolean"
                        ? field.value
                        : field.value === opt.value
                    }
                    className="text-[] focus:ring-[]"
                  />
                  <span
                    className={showError ? "text-[#a51212]" : "text-[#31383e]"}
                  >
                    {opt.label}
                  </span>
                </label>
              ))}
            </div>
          </>
        );

      case "checkbox":
        return (
          <label className="inline-flex items-center space-x-2">
            <input
              type="checkbox"
              {...field}
              checked={field.value}
              className="text-[] focus:ring-[]"
            />
            <span className={showError ? "text-[#a51212]" : "text-[#31383e]"}>
              {label}
            </span>
          </label>
        );

      case "textarea":
        return (
          <div className="relative w-full">
            <Field
              {...field}
              {...props}
              as="textarea"
              className={clsx(inputBase, "placeholder:pl-1")}
              placeholder={placeholder}
              onFocus={() => setIsFocused(true)}
              onBlur={(e: any) => {
                field.onBlur(e);
                setIsFocused(false);
              }}
            />
          </div>
        );

      default:
        return (
          <input
            {...field}
            {...props}
            type={type}
            className={clsx(inputBase, "placeholder:pl-1")}
            placeholder={placeholder}
            onFocus={() => setIsFocused(true)}
            onBlur={(e) => {
              field.onBlur(e);
              setIsFocused(false);
            }}
          />
        );
    }
  };

  // const renderErrorIcon = () => (
  //   <div
  //     className={`absolute right-3 ${type === "tel" ? "top-8" : "top-8"} transform  text-[#a51212] bg-white`}
  //   >
  //     <svg
  //       xmlns="http://www.w3.org/2000/svg"
  //       width="24"
  //       height="24"
  //       viewBox="0 0 24 24"
  //       fill="none"
  //     >
  //       <path
  //         d="M11 15H13V17H11V15ZM11 7H13V13H11V7ZM11.99 2C6.47 2 2 6.48 2 12C2 17.52 6.47 22 11.99 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 11.99 2ZM12 20C7.58 20 4 16.42 4 12C4 7.58 7.58 4 12 4C16.42 4 20 7.58 20 12C20 16.42 16.42 20 12 20Z"
  //         fill="#A51212"
  //       />
  //     </svg>
  //   </div>
  // );

  return (
    <div className="relative w-full">
      {!["checkbox", "radio"].includes(type) && (
        <div className="relative">
          {label && type !== "select" && (
            <label className={labelBase}>{label}</label>
          )}
          {renderInput()}
          {append && !["select"].includes(type) && !showError && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              {append}
            </div>
          )}

          {showError && (
            <div className="text-xs text-[#a51212]">{meta.error}</div>
          )}
        </div>
      )}

      {["checkbox", "radio"].includes(type) && (
        <div className="relative">
          {renderInput()}
          {showError && (
            <div className="absolute -bottom-5 left-0 text-xs text-[#a51212]">
              {meta.error}
            </div>
          )}
        </div>
      )}

      {note && !showError && (
        <div className="text-xs text-gray-500 mt-1 dark:text-gray-400">
          {note}
        </div>
      )}
    </div>
  );
};

export default InputField;
